import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Input, Select, Button, Table, Space, Tag, Badge, message, Drawer, Modal, Popover, PaginationProps } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, MessageOutlined } from '@ant-design/icons';
import { whatsappConnectorAgentApi, AgentConversation, AgentConversationQuery, AgentMessage } from '../services/whatsappConnectorAgentApi';

const { Option } = Select;

const defaultPageSize = 20;

const AgentConversationsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState<AgentConversation[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [messagesTotal, setMessagesTotal] = useState<number>(0);
  const [messagesPage, setMessagesPage] = useState<number>(1);
  const [messagesPageSize, setMessagesPageSize] = useState<number>(20);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailText, setDetailText] = useState<string>('');

  const extractMessageText = (r: any): string => {
    let raw: any = r?.content ?? r?.messages ?? r?.message;
    const tryParse = (s: string) => {
      try { return JSON.parse(s); } catch { return s; }
    };
    if (typeof raw === 'string') raw = tryParse(raw);

    if (raw && typeof raw === 'object' && 'messages' in raw) {
      let msgs: any = (raw as any).messages;
      if (typeof msgs === 'string') msgs = tryParse(msgs);
      if (Array.isArray(msgs)) {
        return msgs.map((it: any) => it?.content ?? it?.text ?? JSON.stringify(it)).join('\n\n');
      }
    }

    if (Array.isArray(raw)) {
      return raw.map((it: any) => it?.content ?? it?.text ?? JSON.stringify(it)).join('\n\n');
    }
    if (raw && typeof raw === 'object') {
      if ('content' in raw && (raw as any).content) return (raw as any).content as string;
      return JSON.stringify(raw, null, 2);
    }
    if (typeof raw === 'string') return raw.replace(/\\n/g, '\n');
    return '-';
  };

  const queryParams: AgentConversationQuery = useMemo(() => {
    const values = form.getFieldsValue();
    return {
      page,
      pageSize,
      channel_type: values.channel_type,
      channel_agent_id: values.channel_agent_id,
      channel_user_id: values.channel_user_id,
      agent_user_id: values.agent_user_id,
      agent_platform_id: values.agent_platform_id,
      agent_id: values.agent_id,
      conversation_id: values.conversation_id,
      conversation_status: values.conversation_status
    };
  }, [form, page, pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await whatsappConnectorAgentApi.getAgentConversations(queryParams);
      setConversations(res.data || []);
      setTotal(res.total || 0);
    } catch (error: any) {
      message.error('加载会话失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    setMessagesLoading(true);
    try {
      const res = await whatsappConnectorAgentApi.getAgentConversationMessages(conversationId, { page: messagesPage, pageSize: messagesPageSize });
      setMessages(res.data || []);
      setMessagesTotal(res.total || 0);
    } catch (error: any) {
      message.error('加载消息失败: ' + error.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    if (drawerOpen && currentConversationId) {
      loadMessages(currentConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, currentConversationId, messagesPage, messagesPageSize]);

  const onSearch = () => {
    setPage(1);
    loadData();
  };

  const onReset = () => {
    form.resetFields();
    setPage(1);
    setPageSize(defaultPageSize);
    loadData();
  };

  const columns = [
    { title: '会话ID', key: 'conversation_id', ellipsis: true, render: (_: any, r: any) => r.conversation_id || r.conversationId || '-' },
    { title: '渠道类型', key: 'channel_type', render: (_: any, r: any) => {
      const v = r.channel_type || r.channelType;
      return v ? <Tag color="blue">{v}</Tag> : '-';
    } },
    { title: '渠道Agent', key: 'channel_agent_id', render: (_: any, r: any) => r.channel_agent_id || r.channelAgentId || '-' },
    { title: '渠道用户', key: 'channel_user_id', render: (_: any, r: any) => r.channel_user_id || r.channelUserId || '-' },
    { title: 'Agent用户', key: 'agent_user_id', render: (_: any, r: any) => r.agent_user_id || r.agentUserId || '-' },
    { title: 'Agent平台', key: 'agent_platform_id', render: (_: any, r: any) => r.agent_platform_id || r.agentPlatformId || '-' },
    { title: 'Agent ID', key: 'agent_id', render: (_: any, r: any) => r.agent_id || r.agentId || '-' },
    { title: '状态', key: 'conversation_status', render: (_: any, r: any) => {
      const v = r.conversation_status || r.conversationStatus;
      return <Badge status={v === 'active' ? 'processing' : v === 'closed' ? 'default' : 'warning'} text={v || '-'} />;
    } },
    { title: '最后消息时间', key: 'last_message_time', render: (_: any, r: any) => r.last_message_at || r.lastMessageTime || r.updated_at || r.updatedAt || '-' },
    {
      title: '操作', key: 'action', width: 120, render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} onClick={() => {
            const cid = record.conversation_id || record.conversationId;
            if (!cid) return;
            setCurrentConversationId(cid);
            setMessagesPage(1);
            setDrawerOpen(true);
          }}>查看消息</Button>
        </Space>
      )
    }
  ];

  const messageColumns = [
    { title: '方向', dataIndex: 'direction', key: 'direction', width: 80, render: (v: string, r: any) => <Tag color={(v || r.direction) === 'outbound' ? 'green' : 'geekblue'}>{v || r.direction}</Tag> },
    { title: '状态', dataIndex: 'status', key: 'status', width: 80 },
    { title: '内容', key: 'content', width: 400, align: 'left' as const, render: (_: any, r: any) => {
      const s = extractMessageText(r);
      const preview = s;
      return (
        <Popover
          placement="topLeft"
          overlayStyle={{ maxWidth: 800 }}
          content={<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{s}</pre>}
        >
          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical' as any,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              lineHeight: 1.6,
              cursor: 'pointer'
            }}
          >
            {preview}
          </span>
        </Popover>
      );
    } },
    { title: '时间', key: 'created_at', width: 120, render: (_: any, r: any) => r.created_at || r.createdAt || '-' },
    { title: '操作', key: 'action', width: 100, render: (_: any, r: any) => (
      <Button type="link" onClick={() => { setDetailText(extractMessageText(r)); setDetailOpen(true); }}>查看</Button>
    ) }
  ];

  const onTableChange: PaginationProps['onChange'] = (p, ps) => {
    setPage(p);
    setPageSize(ps || defaultPageSize);
  };

  return (
    <Card title="Agent 会话" extra={
      <Space>
        <Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>
      </Space>
    }>
      <Form form={form} layout="vertical" onFinish={onSearch}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="channel_type" label="渠道类型">
              <Input placeholder="如 whatsapp" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="channel_agent_id" label="渠道Agent ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="channel_user_id" label="渠道用户ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="agent_user_id" label="Agent用户ID">
              <Input allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="agent_platform_id" label="Agent平台ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="agent_id" label="Agent ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="conversation_id" label="会话ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="conversation_status" label="会话状态">
              <Select allowClear placeholder="请选择">
                <Option value="active">active</Option>
                <Option value="closed">closed</Option>
                <Option value="pending">pending</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onReset}>重置</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
          </Col>
        </Row>
      </Form>

      <Table
        style={{ marginTop: 16 }}
        rowKey={(r: any) => r.conversation_id || r.conversationId || String(r.id)}
        loading={loading}
        columns={columns as any}
        dataSource={conversations}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, showQuickJumper: true }}
        onChange={(pagination) => onTableChange(pagination.current!, pagination.pageSize || defaultPageSize)}
      />

      <Drawer
        title={`会话 ${currentConversationId || ''} 的消息`}
        width={1080}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <Table
          size="middle"
          tableLayout="fixed"
          rowKey={(r: any) => String(r.id || r.message_id || r.messageId || Math.random())}
          loading={messagesLoading}
          columns={messageColumns as any}
          dataSource={messages}
          pagination={{ current: messagesPage, pageSize: messagesPageSize, total: messagesTotal, showSizeChanger: true, showQuickJumper: true }}
          onChange={(pagination) => {
            setMessagesPage(pagination.current!);
            setMessagesPageSize(pagination.pageSize!);
          }}
        />
        <Modal
          title="消息详情"
          open={detailOpen}
          onCancel={() => setDetailOpen(false)}
          footer={<Button type="primary" onClick={() => setDetailOpen(false)}>关闭</Button>}
          width={800}
        >
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{detailText}</pre>
        </Modal>
      </Drawer>
    </Card>
  );
};

export default AgentConversationsPage;


